import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { format } from "date-fns";

const PRESETS = [
  { key: "all",   label: "All Data",     Icon: RotateCcw },
  { key: "today", label: "Today" },
  { key: "week",  label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "7d",    label: "Last 7 Days" },
  { key: "30d",   label: "Last 30 Days" },
];

export default function DateFilter({ value, onChange }) {
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [popOpen, setPopOpen] = useState(false);

  // Reset internal range when external value changes away from custom
  useEffect(() => {
    if (value.preset !== "custom") setRange({ from: undefined, to: undefined });
  }, [value.preset]);

  const apply = () => {
    if (range.from && range.to) {
      onChange({
        preset: "custom",
        start: range.from.toISOString(),
        end: range.to.toISOString(),
      });
      setPopOpen(false);
    }
  };

  const customLabel =
    value.preset === "custom" && range.from && range.to
      ? `${format(range.from, "MMM d")} – ${format(range.to, "MMM d")}`
      : "Custom Range";

  const startHint = range.from
    ? `Selected: ${format(range.from, "MMM d, yyyy")}${range.to ? ` → ${format(range.to, "MMM d, yyyy")}` : " — click an end date"}`
    : "Click to select start date";

  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="date-filter">
      {PRESETS.map((p) => (
        <button
          key={p.key}
          data-testid={`date-preset-${p.key}`}
          onClick={() => onChange({ preset: p.key })}
          className={`px-3 py-1.5 text-xs tracking-wider uppercase border transition-colors flex items-center gap-2
            ${value.preset === p.key
              ? "bg-[#00D4FF] text-[#0A0B10] border-[#00D4FF]"
              : "bg-transparent text-[#94A3B8] border-border hover:border-[#00D4FF]/60 hover:text-white"}`}
        >
          {p.Icon && <p.Icon size={12} />}
          {p.label}
        </button>
      ))}

      <Popover open={popOpen} onOpenChange={setPopOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="date-preset-custom"
            className={`px-3 py-1.5 text-xs tracking-wider uppercase border flex items-center gap-2 transition-colors
              ${value.preset === "custom"
                ? "bg-[#00D4FF] text-[#0A0B10] border-[#00D4FF]"
                : "bg-transparent text-[#94A3B8] border-border hover:border-[#00D4FF]/60 hover:text-white"}`}
          >
            <CalendarIcon size={14} />
            {customLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-[#12141D] border-border p-0 w-auto" align="end">
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={2}
            className="bg-[#12141D] text-white"
          />
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border">
            <div className="text-xs text-muted-foreground" data-testid="date-range-hint">{startHint}</div>
            <button
              data-testid="date-apply-range"
              disabled={!range.from || !range.to}
              onClick={apply}
              className="px-3 py-1.5 text-xs tracking-wider uppercase bg-[#00D4FF] text-[#0A0B10] font-semibold
                hover:bg-[#00B8D4] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Apply Range
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
